<script setup>
import {ref, onMounted, onBeforeUnmount, reactive, onBeforeMount, computed} from 'vue';
import './Home.styl'
import { useActivityTracker } from '../composables/useActivityTracker'
import { useLoadingScreen } from '../composables/useLoadingScreen'

const time = ref(new Date());
const hours = ref('');
const minutes = ref('');
const seconds = ref('');
const ampm = ref('');
const day = ref('');
const date = ref('');
const month = ref('');
const year = ref('');
const interval = ref(null);
const disableTransition = ref(false);

const audioPlayer = ref(null)
const isPlaying = ref((localStorage.getItem('isPlayingRadio') || 'false') === 'true')
const analogWatch = ref((localStorage.getItem('analogWatch') || 'true') === 'true')

const toggleAnalogWatch = () => {
  const newValue = !analogWatch.value;
  analogWatch.value = newValue
  localStorage.setItem('analogWatch', newValue ? 'true' : 'false')
}

const currentStation = ref({})

const { wasActiveInLastHours, getLastActivityTime } = useActivityTracker()

const lastActivity = ref('')
const isActiveRecently = ref(false)

const screenWidth = ref(window.innerWidth)

const updateScreenWidth = () => {
  screenWidth.value = window.innerWidth
}

onMounted(() => {
  window.addEventListener('resize', updateScreenWidth)
})

const updateThemeColor = (color) => {
  // Найти существующий мета-тег
  let metaThemeColor = document.querySelector('meta[name="theme-color"]')

  if (metaThemeColor) {
    // Если существует, изменить content
    metaThemeColor.setAttribute('content', color)
  } else {
    // Если не существует, создать новый
    metaThemeColor = document.createElement('meta')
    metaThemeColor.name = 'theme-color'
    metaThemeColor.content = color
    document.head.appendChild(metaThemeColor)
  }
}

const isTablet = computed(() => screenWidth.value <= 1249)

const stations = reactive([
  { id: 1, name: 'Europa Plus', url: 'http://ep256.hostingradio.ru:8052/europaplus256.mp3' },
  { id: 2, name: 'Radio ENERGY', url: 'http://ic7.101.ru:8000/v3_1' },
  { id: 3, name: 'Record Main', url: 'https://radiorecord.hostingradio.ru/rr_main96.aacp' },
  { id: 4, name: 'Record Russian', url: 'https://radiorecord.hostingradio.ru/rus96.aacp' },
  { id: 5, name: 'Record Gold', url: 'https://radiorecord.hostingradio.ru/gold96.aacp' },
  { id: 6, name: 'Record EDM', url: 'https://radiorecord.hostingradio.ru/club96.aacp' },
  { id: 7, name: 'Record Trap', url: 'https://radiorecord.hostingradio.ru/trap96.aacp' },
  { id: 8, name: 'Record Phonk', url: 'https://radiorecord.hostingradio.ru/phonk96.aacp' },
  { id: 9, name: 'LoFi Radio', url: 'https://live.lofiradio.ru/lofi_mp3_320' },
  { id: 10, name: 'India', url: 'https://radio.digitalmalayali.in/listen/stream/radio.mp3' },
  { id: 11, name: 'Relax FM', url: 'https://srv21.gpmradio.ru:8443/stream/air/aac/64/200' },
  { id: 12, name: 'Ретро FM 80-е', url: 'https://retro80.hostingradio.ru:8025/retro80-128.mp3' },
  { id: 13, name: 'Кыргызстан Обондору', url: 'https://cdn.radioplayer.kg:8443/obondoru64' },
  { id: 14, name: 'Deep FM', url: 'https://deep.amgradio.ru/DeepFM' },
  { id: 15, name: 'Монте-Карло - Без слов', url: 'https://montecarlo.hostingradio.ru/muzykabezslov96.aacp' },
  { id: 16, name: 'Record Deep', url: 'https://radiorecord.hostingradio.ru/deep96.aacp' },
])

const {
  showLoadingScreen,
  loadingText,
  performCheck,
  closeLoadingScreen
} = useLoadingScreen()

const currentStationIndex = ref(Number(localStorage.getItem('currentStationIndex') || '0'))

currentStation.value = stations[currentStationIndex.value] ?? 0

const togglePlay = () => {
  if (isPlaying.value) {
    localStorage.setItem('isPlayingRadio', 'false')
    audioPlayer.value.pause()
  } else {
    audioPlayer.value.play()
    localStorage.setItem('isPlayingRadio', 'true')
  }
  isPlaying.value = !isPlaying.value
}

const changeStation = () => {
  const wasPlaying = isPlaying.value
  audioPlayer.value.pause()
  audioPlayer.value.load()

  currentStationIndex.value = stations.findIndex(s => s.id === currentStation.value.id)
  localStorage.setItem('currentStationIndex', String(currentStationIndex.value))

  if (wasPlaying) {
    audioPlayer.value.play()
  }
}

const toggleNext = () => {
  const wasPlaying = isPlaying.value

  currentStationIndex.value = (currentStationIndex.value + 1) % stations.length
  currentStation.value = stations[currentStationIndex.value]
  localStorage.setItem('currentStationIndex', String(currentStationIndex.value))

  audioPlayer.value.pause()

  audioPlayer.value.load()

  if (wasPlaying) {
    audioPlayer.value.play()
  }
}

const togglePrev = () => {
  const wasPlaying = isPlaying.value

  currentStationIndex.value = (currentStationIndex.value - 1) % stations.length
  currentStation.value = stations[currentStationIndex.value]
  localStorage.setItem('currentStationIndex', String(currentStationIndex.value))

  audioPlayer.value.pause()

  audioPlayer.value.load()

  if (wasPlaying) {
    audioPlayer.value.play()
  }
}

const needWater = ref((localStorage.getItem('needWaterFlag') || 'false') === 'true');
const needWaterSaver = ref(localStorage.getItem('needWaterSaver') || '');

const needTraining = ref((localStorage.getItem('needTrainingFlag') || 'false') === 'true');
const needTrainingSaver = ref(localStorage.getItem('needTrainingSaver') || '');

const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const dateString = new Date().toISOString().slice(0, 10).split('-').reverse().join('_')
const tswk = "total_seconds_work_" + dateString
const tssk = "total_seconds_self_" + dateString
const totalSecondsWork = ref(Number(localStorage.getItem(tswk) || 0))
const totalSecondsSelf = ref(Number(localStorage.getItem(tssk) || 0))

const hoursW = ref("");
const minutesW = ref("");
const secondsW = ref("");

const hoursS = ref("");
const minutesS = ref("");
const secondsS = ref("");

const ping = ref(false);

const workActive = ref((localStorage.getItem('workActive') === 'true') || false);
const selfActive = ref((localStorage.getItem('selfActive') === 'true') || false);

const dailyWaterCount = ref(Number(localStorage.getItem('dailyWaterCount_' + new Date().toISOString().slice(0, 10).split('-').reverse().join('_')) || 0));
const dailyCoffeeCount = ref(Number(localStorage.getItem('dailyCoffeeCount_' + new Date().toISOString().slice(0, 10).split('-').reverse().join('_')) || 0));

const convertSeconds = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return {
    hours: hours.toString().padStart(2, '0'),
    minutes: minutes.toString().padStart(2, '0'),
    seconds: seconds.toString().padStart(2, '0')
  };
};

const updateTime = () => {

  if (!wasActiveInLastHours(1)) {
    // ToDo White Scree With Memas
    return;
  }

  time.value = new Date();

  // Format hours for 12-hour clock
  let h = time.value.getHours();
  ampm.value = h >= 12 ? 'PM' : 'AM';
  // h = h % 12 || 12;
  hours.value = h < 10 ? '0' + h : h.toString();

  // Format minutes and seconds
  minutes.value = time.value.getMinutes() < 10 ? '0' + time.value.getMinutes() : time.value.getMinutes().toString();
  seconds.value = time.value.getSeconds() < 10 ? '0' + time.value.getSeconds() : time.value.getSeconds().toString();
  // console.log(seconds.value)
  disableTransition.value = seconds.value == "00"

  // Format date
  day.value = days[time.value.getDay()];
  date.value = time.value.getDate();
  month.value = months[time.value.getMonth()];
  year.value = time.value.getFullYear();

  if (workActive.value) {
    ++totalSecondsWork.value;
    localStorage.setItem(tswk, totalSecondsWork.value)
    document.title = `Work: ${hoursW.value}:${minutesW.value}:${secondsW.value}`;
  }

  if (selfActive.value) {
    ++totalSecondsSelf.value;
    localStorage.setItem(tssk, totalSecondsSelf.value)
    document.title = `Self: ${hoursS.value}:${minutesS.value}:${secondsS.value}`;
  }

  if (!selfActive.value && !workActive.value) {
    document.title = `Paused | Time Tracker`;
  }

  const convertedWork = convertSeconds(totalSecondsWork.value)
  hoursW.value = convertedWork.hours
  minutesW.value = convertedWork.minutes
  secondsW.value = convertedWork.seconds

  const convertedSelf = convertSeconds(totalSecondsSelf.value)
  hoursS.value = convertedSelf.hours
  minutesS.value = convertedSelf.minutes
  secondsS.value = convertedSelf.seconds

  let saveTraining = hours.value + ':50' // 50
  if (
    time.value.getMinutes() === 50 // 50
    && needTrainingSaver.value !== saveTraining
  ) {
    needTrainingSaver.value = saveTraining
    localStorage.setItem('needTrainingSaver', saveTraining)

    needTraining.value = true
    localStorage.setItem("needTrainingFlag", "true")
  }

  let saveWater = hours.value + ':30' // 30
  if (
    time.value.getMinutes() === 30 // 30
    && needWaterSaver.value !== saveWater
  ) {
    needWaterSaver.value = saveWater
    localStorage.setItem('needWaterSaver', saveWater)
    needWater.value = true
    localStorage.setItem("needWaterFlag", "true")
  }
  saveWater = hours.value + ':00'
  if (
    time.value.getMinutes() === 0
    && needWaterSaver.value !== saveWater
  ) {
    needWaterSaver.value = saveWater
    localStorage.setItem('needWaterSaver', saveWater)
    needWater.value = true
    localStorage.setItem("needWaterFlag", "true")
  }

  const isOdd = seconds.value % 2 === 1;

  if (
    needWater.value
    || needTraining.value
    // || true
  ) {
    ping.value = true;

    if (needWater.value) {
      document.title = isOdd
        ? "💧 Need Water 💧"
        : "❗️ Drink Water ❗️";
    }
    if (needTraining.value) {
      document.title = isOdd
        ? "💪 Work Hard 💪"
        : "❗️ Go Training ❗️";
    }

  } else {
    ping.value = false;
  }

  if (needTraining.value) {
    updateThemeColor('lawngreen')
  } else if (needWater.value) {
    updateThemeColor('blue')
  } else {
    updateThemeColor('#1a1f35')
  }
};

const checkActivity = () => {
  isActiveRecently.value = wasActiveInLastHours(5)
  const lastTime = getLastActivityTime()
  lastActivity.value = lastTime ? lastTime.toLocaleString() : 'Нет данных'
}

onMounted(async () => {
  await performCheck()
})

onMounted(() => {
  updateTime();
  interval.value = setInterval(updateTime, 1000);
  checkActivity()
  if (isPlaying.value) {
    audioPlayer.value.play()
  }
});

onBeforeUnmount(() => {
  clearInterval(interval.value);
});

// background: linear-gradient(90deg, #14ffe9, #ffeb3b, #ff00e0);
// hours::after, .minutes::after, .seconds::after

const toggleActivate = (isWork) => {
  if (isWork) {
    if (selfActive.value) {
      localStorage.setItem("selfActive", "false")
      selfActive.value = false
      localStorage.setItem("workActive", "true")
      workActive.value = true;
    } else {
      localStorage.setItem("workActive", workActive.value ? "false" : "true")
      workActive.value = !workActive.value;
    }
  } else {
    if (workActive.value) {
      localStorage.setItem("selfActive", "true")
      selfActive.value = true;
      localStorage.setItem("workActive", "false")
      workActive.value = false;
    } else {
      localStorage.setItem("selfActive", selfActive.value ? "false" : "true")
      selfActive.value = !selfActive.value;
    }
  }
}

const iDrink = () => {
  needWater.value = false
  localStorage.setItem("needWaterFlag", "false")
  let newVal = dailyWaterCount.value + 0.1;
  dailyWaterCount.value = newVal;
  localStorage.setItem(
    "dailyWaterCount_" + new Date().toISOString().slice(0, 10).split('-').reverse().join('_'),
    String(newVal)
  )
}

const iDrinkCoffee = () => {
  let newVal = dailyCoffeeCount.value + 1;
  dailyCoffeeCount.value = newVal;
  localStorage.setItem(
    "dailyCoffeeCount_" + new Date().toISOString().slice(0, 10).split('-').reverse().join('_'),
    String(newVal)
  )
}

const iTrain = () => {
  needTraining.value = false
  localStorage.setItem("needTrainingFlag", "false")
}

const peeCount = ref(Number(localStorage.getItem(
  'peeCount_' + new Date().toISOString().slice(0, 10).split('-').reverse().join('_')) || 0));
const shitCount = ref(Number(localStorage.getItem(
  'shitCount_' + new Date().toISOString().slice(0, 10).split('-').reverse().join('_')) || 0));

const addPee = () => {
  localStorage.setItem(
    "peeCount_" + new Date().toISOString().slice(0, 10).split('-').reverse().join('_'),
    ++peeCount.value
  )
}

const skipWater = () => {
  needWater.value = false
  localStorage.setItem('needWaterFlag', 'false')
}

const skipTraining = () => {
  needTraining.value = false
  localStorage.setItem('needTrainingFlag', 'false')
}

const addShit = () => {
  localStorage.setItem(
    "shitCount_" + new Date().toISOString().slice(0, 10).split('-').reverse().join('_'),
    ++shitCount.value
  )
}

const ale = () => {
  alert('Coming soon...');
}

const openAsSingleTab = () => {
  const url = window.location.href;
  window.open(url, '_blank', 'menubar=no,toolbar=no,location=no,status=no');
}

// function checkWindowFeatures() {
//   const features = {
//     menubar: window.menubar.visible,
//     toolbar: window.toolbar.visible,
//     location: window.locationbar.visible,
//     status: window.statusbar.visible,
//     scrollbars: window.scrollbars.visible,
//     personalbar: window.personalbar.visible
//   };
//
//   return features;
// }

// Использование
// const windowFeatures = checkWindowFeatures();
// console.log('windowFeatures:', windowFeatures);

const subStrMonth = (month) => {
  return month.slice(0, 4);
}

const hideWithoutToolbarButton = ((Number(window.menubar.visible) +
  Number(window.toolbar.visible) +
  Number(window.locationbar.visible) +
  Number(window.statusbar.visible) +
  Number(window.scrollbars.visible) +
  Number(window.personalbar.visible)) < 3) ||
  (window.innerWidth > screen.width && window.innerHeight > screen.height)
  || window.innerWidth < 1500


const pageReload = () => {
  window.location.reload(true)
}
</script>

<template>
  <div class="time">

    <div
      v-if="showLoadingScreen"
      class="loading-screen"
    >
      <div class="content">

<!--        <h2>{{ loadingText }}</h2>-->

        <button class="close btn btn-outline-dark rounded-circle p-3 lh-1" type="button" v-on:click="closeLoadingScreen('skip')">
          <svg width="24" height="24" fill="currentColor" aria-hidden="true">
            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L12 11.293l6.646-6.647a.5.5 0 0 1 .708.708L12.707 12l6.647 6.646a.5.5 0 0 1-.708.708L12 12.707l-6.646 6.647a.5.5 0 0 1-.708-.708L11.293 12 4.646 5.354a.5.5 0 0 1 0-.708z"/>
          </svg>
          <span class="visually-hidden">Dismiss</span>
        </button>

        <h2 class="good-morning">Доброе утро!</h2>
        <h1 class="you-can-huge-things">Мы уверены, что сегодня <br>у тебя обязательно <b>всё получится</b>!</h1>
        <h2 class="how-you-sleep-today">Расскажи, как тебе <b>спалось</b> сегодня?</h2>

        <div class="buttons">
          <button @click="() => closeLoadingScreen('+')" class="btn btn-danger rounded-pill px-3">
            +
          </button>
          <button @click="() => closeLoadingScreen('Я не спал')" class="btn btn-danger rounded-pill px-3">
            Я не спал
          </button>
          <button @click="() => closeLoadingScreen('Не Выспался')" class="btn btn-warning rounded-pill px-3">
            Не Выспался
          </button>
          <button @click="() => closeLoadingScreen('Как обычно')" class="btn btn-primary rounded-pill px-3">
            Как обычно
          </button>
          <button @click="() => closeLoadingScreen('Отлично')" class="btn btn-success rounded-pill px-3">
            Отлично
          </button>
        </div>
      </div>

    </div>

    <div class="time__dynamic-border" :class="{
      'time__dynamic-border_ping': ping,
      'time__dynamic-border_ping-training': needTraining,
      'time__dynamic-border_ping-water': !needTraining && needWater, // todo: fix
    }">
    </div>

    <div class="time__container">

      <div class="time__container__header">

        <div class="time__container__header__time-date">
          <img src="../assets/logo.svg" alt="Logo" width="100" class="app-logo" style="height: 70px;align-self: center;" v-on:click="pageReload"/>

          <div class="digital-time digital-time-top">
            <span class="hours">{{ hours }}</span>
            <span class="time-divider">:</span>
            <span class="minutes">{{ minutes }}</span>
          </div>

          <div class="time__container__header__time-date__date">
            <span class="time__container__header__time-date__date__day">{{ day }}</span>
            <span class="time__container__header__time-date__date__date-details">{{ subStrMonth(month) }} {{ date }}, {{ year }}</span>
          </div>

          <div class="without-toolbar-button-container" style="display: flex; margin-left: 26px;" :class="{ 'd-none': hideWithoutToolbarButton }">
            <div v-on:click="openAsSingleTab" class="btn btn-outline-primary" style="margin-top: auto;margin-bottom: auto;align-self: center;">Without<br>Toolbar</div>
          </div>
        </div>

        <div class="radio-player">
          <audio ref="audioPlayer" preload="none">
            <source :src="currentStation.url" type="audio/mpeg">
          </audio>

          <div class="controls" style="display: flex;gap: 10px;flex-direction: column;align-items: center">
            <div style="max-width: 200px">
              <select style="min-width: 180px" class="form-select" v-model="currentStation" @change="changeStation">
                <option v-for="station in stations" :value="station">
                  {{ station.name }}
                </option>
              </select>
            </div>
            <div style="display: flex; justify-content: space-between;min-width: 200px;">
              <button style="display: flex" class="btn btn-outline-light" @click="togglePrev" >
                &lt;
              </button>
              <button class="btn btn-outline-light" @click="togglePlay" style="min-width: 110px;">
                {{ isPlaying ? 'Pause' : 'Play' }}
              </button>
              <button style="display: flex" class="btn btn-outline-light" @click="toggleNext">
                &gt;
              </button>
            </div>
          </div>
        </div>

        <div class="fake-last-header">
        </div>

      </div>

      <div class="time__container__body">
        <div class="time__container__body__raws">

          <div class="small-selectable-timers">
            <div class="digital-time digital-time-selectable digital-time-work" :class="{ active: workActive }" v-on:click="() => toggleActivate(true)">
              <div class="time-hover">
                <div style="width: 150px;font-size: 24px; font-weight: 800;min-height: 28px;margin-bottom: 6px;">
                  <div style="line-height: 24px;">Work:</div>
                </div>
                <div class="time-numbers">
                  <span class="hours" v-if="!isTablet || hoursW !== '00'">{{ hoursW }}</span>
                  <span class="time-divider" v-if="!isTablet || hoursW !== '00'">:</span>
                  <span class="minutes">{{ minutesW }}</span>
                  <span class="time-divider time-divider-seconds" :style="{display: hoursW === '00' ? 'block' : 'none'}">:</span>
                  <span class="seconds" :style="{display: hoursW === '00' ? 'block' : 'none'}">{{ secondsW }}</span>
                </div>
              </div>
            </div>

            <div class="digital-time digital-time-selectable digital-time-self digital-time-self_under-work" :class="{ active: selfActive }" v-on:click="() => toggleActivate()">
              <div class="time-hover">
                <div style="width: 150px;font-size: 24px; font-weight: 800;min-height: 28px; margin-bottom: 6px">
                  <div style="line-height: 24px;">Self:</div>
                </div>
                <div class="time-numbers">
                  <span class="hours" v-if="hoursS !== '00'">{{ hoursS }}</span>
                  <span class="time-divider" v-if="hoursS !== '00'">:</span>
                  <span class="minutes">{{ minutesS }}</span>
                  <span class="time-divider time-divider-seconds" :style="{display: hoursS === '00' ? 'block' : 'none'}">:</span>
                  <span class="seconds" :style="{display: hoursS === '00' ? 'block' : 'none'}">{{ secondsS }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="digital-time digital-time-selectable digital-time-self digital-time-self__second" :class="{ active: selfActive }" v-on:click="() => toggleActivate()">
            <div class="time-hover">
              <div style="width: 150px;font-size: 24px; font-weight: 800;min-height: 28px; margin-bottom: 6px">
                <div style="line-height: 24px;">Self:</div>
              </div>
              <div class="time-numbers">
                <span class="hours">{{ hoursS }}</span>
                <span class="time-divider">:</span>
                <span class="minutes">{{ minutesS }}</span>
                <span class="time-divider time-divider-seconds">:</span>
                <span class="seconds">{{ secondsS }}</span>
              </div>
            </div>
          </div>

          <div class="watch-and-counters-container">

            <div style="position: relative;" class="watch-analog-container">


              <div style="transform: scale(1.2);margin-left: auto;margin-right: auto;margin-top: 40px;" class="clock-circle" :class="{'d-none': !analogWatch}">
                <div class="clock-face">
                  <div class="clock-center"></div>

                  <div class="clock-hand hour-hand" :style="{ transform: `rotate(${Number(hours) * 30 + Number(minutes) / 2}deg)` }"></div>
                  <div class="clock-hand minute-hand" :style="{ transform: `rotate(${Number(minutes) * 6}deg)` }"></div>
                  <div class="clock-hand second-hand" :style="{
              transform: `rotate(${Number(seconds) * 6}deg)`,
              transition: disableTransition ? 'none' : 'transform 0.95s linear'
            }"></div>

                  <div class="marker hour-marker" v-for="n in 12" :key="n" :style="{ transform: `rotate(${n * 30}deg)` }">
                    <span :style="{ transform: `rotate(${-n * 30}deg)` }">{{ n }}</span>
                  </div>
                </div>
              </div>

              <div style="position: absolute; top: 0; right: 0;" v-on:click="toggleAnalogWatch">
                <div class="btn btn-outline-light">👀</div>
              </div>

            </div>

            <div class="counters-container">
              <h1 style="font-size: 24px; font-weight: 800; ">Counters:</h1>
              <div class="counter-wrapper counter-wrapper-water" style="margin-left: auto;">
                <div style="line-height: 38px; display: flex; font-size: 20px; font-weight: 800;margin-left: auto; text-align: right;">{{ dailyWaterCount.toFixed(2) }}<span style="margin-left: 6px;">L</span><span class="btn btn-outline-info hide-me" style="margin-left: 16px" v-on:click="iDrink">💧</span></div>
              </div>
              <div class="counter-wrapper counter-wrapper-water" style="margin-left: auto;">
                <div style="line-height: 38px; display: flex; font-size: 20px; font-weight: 800;margin-left: auto; text-align: right;">{{ dailyCoffeeCount }}<span style="margin-left: 6px;"></span><span class="btn btn-outline-info hide-me btn-coffee" style="border-color: #a23800; margin-left: 16px" v-on:click="iDrinkCoffee">☕</span></div>
              </div>
              <div class="counter-wrapper counter-wrapper-water" style="display: none;">
                <div>Pee<span class="hide-me" style="visibility: hidden;">ee Count: {{ peeCount }} <span class="btn btn-outline-warning" style="margin-left: 3px" v-on:click="addPee">+</span></span></div>
              </div>

              <div class="counter-wrapper counter-wrapper-water" style="display: none;">
                <div>Shit<span class="hide-me" style="visibility: hidden;">hit Count: {{ shitCount }} <span class="btn btn-outline-warning" style="margin-left: 3px" v-on:click="addShit">+</span></span></div>
              </div>

            </div>
          </div>
        </div>

      </div>

    </div>

    <div class="notifications">
      <div class="one-notification auth-notification" style="">
        <div class="notification-wrapper notification-wrapper-for-first water-wrapper" style="border-color: white;border-width: 1px;border-radius: 12px" v-if="true">
          <div style="font-weight: 800">Привет, Username!</div><div class="btn btn-outline-light" style="margin-right: 6px;border: 1px solid white; color: white; width: 105px;font-weight: 800;" v-on:click="ale">Вход</div>
        </div>
      </div>

      <div class="one-notification" v-if="needWater">
        <div class="btn btn-outline-water one-notification__on-hover" v-on:click="skipWater">
          <img src="../assets/cross.svg" alt="x"/>
        </div>
        <div class="notification-wrapper water-wrapper">
          <div style="font-weight: 800;">Выпить воды</div>
          <div class="btn btn-outline-info btn-notification-confirm" style="width: 105px;font-weight: 800;" v-on:click="iDrink">Выпил</div>
        </div>
      </div>

      <div class="one-notification" v-if="needTraining">
        <div class="btn btn-outline-training one-notification__on-hover" v-on:click="skipTraining">
          <img src="../assets/cross.svg" alt="x"/>
        </div>
        <div class="notification-wrapper training-wrapper">
          <div style="font-weight: 800;">Разминка</div>
          <div class="btn btn-outline-success btn-notification-confirm" style="width: 105px;font-weight: 800;" v-on:click="iTrain">Размялся</div>
        </div>
      </div>

    </div>

  </div>
</template>

<style lang="stylus" scoped>
.time
  color white
  height 100vh;
  border 20px solid transparent
  background #1a1f35

@keyframes borderPulse
  0%
    border-width 0
  50%
    border-width 20px
  100%
    border-width 0

.time__dynamic-border_ping
  animation: borderPulse 1s ease-in-out infinite alternate;

.time__dynamic-border_ping-training
  border-color lawngreen !important

.time__dynamic-border_ping-water
  border-color blue !important

.time__dynamic-border
  border-width 0
  border-style solid
  border-color transparent
  position fixed
  top 0
  left 0
  height 100vh
  width 100vw

.time__container
  position fixed
  display flex
  flex-direction column
  padding 15px
  height 100%
  gap 30px

  &__header
    display flex
    justify-content space-between
    min-width calc(100vw - 70px)

    > *
      width 33%

    &__time-date
      display flex
      gap 14px
      margin-left 10px


      &__date
        display flex
        flex-direction column
        margin-top auto
        margin-bottom auto
        font-size 20px

        &__day
          font-weight bold
          margin-right 5px
          color #ffeb3b

        &__date-details
          color #14ffe9
          min-width 130px

  &__body
    color white
    margin-left 10px

    &__raws
      display grid
      //grid-template-columns repeat(3, 1fr)
      grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
      //grid-template-columns: repeat(2, minmax(360px, 1fr));

      height: calc(100vh - 40px - 30px - 70px - 60px);
      gap: 40px;

// ToDo Refactor segment below
.notifications {
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: white;
  position: absolute;
  top: 30px;
  right: 30px;
}

.notification-wrapper-for-first {
  //width  570px !important;
}

.notification-wrapper {
  background-color #1b2337
  width: 400px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 18px;
  //background-color: #252f4d;
  margin-left auto
  max-width calc(100vw - 60px)
  flex-wrap: wrap;
  gap: 12px;
}


.water-wrapper {
  border: 8px solid blue
  padding: 12px 12px 12px 25px
}

.training-wrapper {
  border: 8px solid lawngreen
  padding: 12px 12px 12px 25px
}
// ToDo Refactor segment above

.digital-time-top
  margin-top auto
  margin-bottom auto

//.digital-time-selectable
//  min-width 360px

.digital-time
  display flex
  color white
  //text-shadow 0 0 6px rgba(255, 255, 255, 0.5)
  gap 6px

.time-divider
  font-size 45px
  font-weight 800
  user-select none

.radio-player
  max-width 410px
  margin-top auto
  margin-bottom auto

.digital-time-selectable
  height: fit-content;

.time-hover
  transition outline 0.5s ease;
  outline 2px solid transparent;
  outline-offset 15px;
  border-radius 10px;

.digital-time-selectable:hover .time-hover
  outline 2px solid white;

.time-numbers
  margin-bottom 6px
  display flex
  gap 6px

.hours, .minutes, .seconds
  color: white;
  outline: 4px solid transparent;
  border: 4px solid transparent;
  border-radius: 16px;
  padding: 10px 15px;
  font-size 45px
  font-weight 800
  height: 78px;
  line-height: 50px;
  min-width: 100px;
  text-align: center;
  background: rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
  user-select: none;

.active .hours, .active .minutes, .active .seconds
  background linear-gradient(#39415a, #39415a) padding-box, linear-gradient(90deg, #14ffe9, #ffeb3b, #ff00e0) border-box

.one-notification
  display flex
  margin-left: auto;

.auth-notification
  width 100%

.btn-outline-light:hover
  color black !important

.loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.content {
  text-align: center;
}

.content h2 {
  margin-bottom: 2rem;
  font-size: 1.5rem;
  color: #333;
}

.buttons
  font-size 18px
  display flex
  gap 12px
  justify-content center
  
.close
  position absolute
  top 40px;
  right 40px

.bi
  fill black !important

.close-icon::before {
  content: "×";
  font-size: 24px;
  line-height: 1;
}


cross-radius = 16px

.one-notification__on-hover
  visibility hidden
  border-top-left-radius cross-radius
  border-bottom-left-radius cross-radius
  border-top-right-radius 0
  border-bottom-right-radius 0
  border-width 8px
  border-style solid
  border-right: 0;
  align-self center
  padding-bottom 5px
  padding-left 5px
  padding-right 5px
  padding-top 5px
  height fit-content
  background-color #252f4d

.one-notification:hover .one-notification__on-hover
  visibility visible

.counter-wrapper:hover .hide-me
  visibility: visible !important;

.d-none
  display none !important

.btn-outline-water
  border-color blue

.btn-outline-training
  border-color lawngreen

.clock-circle {
  position: relative;
  width: 300px;
  height: 300px;
  margin-bottom: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.clock-circle::before {
  content: '';
  position: absolute;
  width: 320px;
  height: 320px;
  background: linear-gradient(#14ffe9, #ffeb3b, #ff00e0);
  border-radius: 50%;
}

.clock-circle::after {
  content: '';
  position: absolute;
  width: 320px;
  height: 320px;
  background: linear-gradient(#14ffe9, #ffeb3b, #ff00e0);
  border-radius: 50%;
}

.clock-face {
  position: relative;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: #344060;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.clock-center {
  position: absolute;
  width: 15px;
  height: 15px;
  background: #ff00e0;
  border-radius: 50%;
  z-index: 10;
}

.clock-hand {
  position: absolute;
  transform-origin: bottom center;
  z-index: 5;
  bottom: 50%;
  border-radius: 10px;
}

.hour-hand {
  width: 8px;
  height: 80px;
  background: #ffeb3b;
  transition: transform 0.3s linear;
}

.minute-hand {
  width: 6px;
  height: 110px;
  background: #14ffe9;
  transition: transform 0.3s linear;
}

.second-hand {
  width: 3px;
  height: 120px;
  background: #ff00e0;
}


.marker {
  position: absolute;
  width: 100%;
  height: 100%;
  text-align: center;
  transform-origin: center;
}

.hour-marker span {
  position: absolute;
  top: 20px;
  display: inline-block;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: bold;
}

.good-morning
  //

.you-can-huge-things
  color black
  margin-bottom 40px
  line-height 70px

.how-you-sleep-today
  margin-bottom 48px

.digital-time-self_under-work
  display none

.counters-container
  gap 8px
  display flex
  flex-direction column

.watch-and-counters-container
  display flex
  flex-direction column
  justify-content space-between
  padding-bottom 20px
  margin-right 16px

.btn-coffee:hover
  background-color rgb(162, 56, 0) !important;

.btn-notification-confirm
  min-width 110px

</style>
